import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ROLE_LABELS } from "@/lib/apiClient";
import {
    SquaresFour,
    Megaphone,
    UsersFour,
    Buildings,
    FileText,
    CheckSquare,
    CurrencyDollar,
    EnvelopeSimple,
    Lightning,
    SignOut,
    Bell,
    Receipt,
    Envelope,
    Warning,
    Stack,
    Copy,
    Wrench,
} from "@phosphor-icons/react";
import NotificationsBell from "@/components/NotificationsBell";

const ALL_LINKS = [
    { to: "/", label: "Dashboard", icon: SquaresFour, roles: "*" },
    { to: "/campaigns", label: "Campaigns", icon: Megaphone, roles: ["super_admin", "client_servicing", "execution", "execution_head", "delivery", "delivery_head", "reporting", "accounts"] },
    { to: "/detailed-approvals", label: "Detailed approvals", icon: CheckSquare, roles: ["super_admin", "execution_head", "execution"] },
    { to: "/reporting-numbers", label: "Reporting numbers", icon: FileText, roles: ["super_admin", "delivery", "delivery_head", "reporting"] },
    { to: "/settings/email", label: "Email settings", icon: EnvelopeSimple, roles: ["super_admin"] },
    { to: "/anomalies", label: "Alerts", icon: Warning, roles: ["super_admin", "execution_head", "delivery_head", "client_servicing", "delivery", "execution"] },
    { to: "/billing", label: "Billing", icon: CurrencyDollar, roles: ["super_admin", "accounts", "client_servicing"] },
    { to: "/digest", label: "Weekly Digest", icon: Envelope, roles: ["super_admin", "client_servicing", "execution_head", "accounts"] },
    { to: "/clients", label: "Clients", icon: Buildings, roles: ["super_admin", "client_servicing", "accounts", "execution_head"] },
    { to: "/templates", label: "Templates", icon: Copy, roles: ["super_admin", "client_servicing"] },
    { to: "/platforms", label: "Platforms", icon: Stack, roles: ["super_admin"] },
    { to: "/maintenance", label: "Maintenance", icon: Wrench, roles: ["super_admin"] },
    { to: "/users", label: "Team", icon: UsersFour, roles: ["super_admin"] },
    { to: "/activity", label: "Activity", icon: Lightning, roles: ["super_admin", "execution_head", "client_servicing", "execution", "delivery_head", "reporting", "accounts"] },
    { to: "/portal", label: "My Reports", icon: FileText, roles: ["client_user"] },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const visible = ALL_LINKS.filter(
        (l) => l.roles === "*" || l.roles.includes(user?.role),
    );

    const onLogout = async () => {
        await logout();
        navigate("/login");
    };

    return (
        <div className="min-h-screen bg-[var(--c-bg)] text-slate-900">
            {/* Sidebar */}
            <aside
                data-testid="sidebar"
                className="hidden md:flex fixed top-0 left-0 h-screen w-64 border-r border-slate-200 bg-white flex-col z-30"
            >
                <div className="px-5 py-6 border-b border-slate-200 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <span className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-display font-bold">
                            P
                        </span>
                        <div className="flex flex-col leading-tight">
                            <span className="font-display text-base tracking-tight">Pulse</span>
                            <span className="overline" style={{ fontSize: 9 }}>Campaign Ops</span>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
                    <div className="px-5 pt-2 pb-2 overline">Workspace</div>
                    {visible.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-2 ${
                                    isActive
                                        ? "bg-slate-900 text-white border-slate-900"
                                        : "border-transparent text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                                }`
                            }
                        >
                            <l.icon size={18} weight="regular" />
                            <span>{l.label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="border-t border-slate-200 px-5 py-4">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center font-mono text-sm">
                            {user?.name?.[0] || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate">{user?.name}</div>
                            <div className="overline truncate" style={{ fontSize: 9 }}>
                                {ROLE_LABELS[user?.role] || user?.role}
                            </div>
                        </div>
                        <button
                            data-testid="logout-button"
                            onClick={onLogout}
                            className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                            title="Sign out"
                        >
                            <SignOut size={18} />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2">
                    <span className="w-7 h-7 bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm">P</span>
                    <span className="font-display text-base">Pulse</span>
                </Link>
                <div className="flex items-center gap-2">
                    <NotificationsBell />
                    <button onClick={onLogout} className="p-1.5 text-slate-600"><SignOut size={20} /></button>
                </div>
            </div>

            {/* Main area */}
            <main className="md:ml-64 min-h-screen">
                <div className="hidden md:flex sticky top-0 z-20 bg-white border-b border-slate-200 px-8 py-3 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="overline">tenant</span>
                        <span className="font-mono text-sm text-slate-900">pulse-agency</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <NotificationsBell />
                    </div>
                </div>
                <div className="p-6 md:p-10 max-w-[1600px]">{children}</div>
            </main>
        </div>
    );
}
